import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  FileSignature,
  Lock,
  CheckCircle,
  ArrowRight,
  Upload,
  Users,
  Database,
  LayoutDashboard,
  History,
  Play,
  UserCheck,
  PenTool,
  Stamp,
  FileText,
  Send,
  Video,
  Layers,
  Zap,
  Globe,
  QrCode,
  ScanLine,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/signO/ui/Navbar";
import Footer from "../../components/signO/ui/Footer";
import FloatingLines from "../../components/signO/ui/FloatingLines";
import LetterGlitch from "../../components/signO/ui/LetterGlitch";
import ScrollReveal from "../../components/signO/ui/ScrollReveal";
import ScrollFloat from "../../components/signO/ui/ScrollFloat";
import FuzzyText from "../../components/signO/ui/FuzzyText";
import ElectricBorder from "../../components/signO/ui/ElectricBorder";
import CircularFlow from "../../components/signO/ui/CircularFlow";
import CardSwap, { Card } from "../../components/signO/ui/CardSwap";

const Home = () => {
  const revealImgRef = useRef(null);
  const editorRef = useRef(null);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-dark text-light overflow-x-hidden">
      {/* Hero Section with FloatingLines */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <FloatingLines
            enabledWaves={["top", "middle", "bottom"]}
            lineCount={[10, 15, 20]}
            lineDistance={[8, 6, 4]}
            bendRadius={5.0}
            bendStrength={-0.5}
            interactive={true}
            parallax={true}
            linesGradient={["#6366f1", "#a855f7", "#ec4899"]}
          />
        </div>

        {/* Overlay for better text visibility */}
        <div className="absolute inset-0 z-0 bg-dark/40 backdrop-blur-[1px]"></div>

        {/* Gradient Fade to merge with next section */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-dark to-transparent z-10 pointer-events-none"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <h1 className="text-6xl md:text-8xl font-bold mb-6 tracking-tight drop-shadow-2xl">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-gray-100 to-gray-300">
                SignO
              </span>
            </h1>
            <p className="text-xl md:text-3xl text-gray-200 mb-12 max-w-4xl mx-auto font-light leading-relaxed drop-shadow-lg">
              The Future of Secure Digital Signatures <br />
              <span className="text-primary font-semibold">
                Blockchain Protected. KYC Verified
              </span>
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <Link
                to="/signup"
                className="group px-8 py-4 bg-primary hover:bg-primary/90 rounded-full text-lg font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(99,102,241,0.5)] hover:shadow-[0_0_30px_rgba(99,102,241,0.7)]"
              >
                Start Free Trial{" "}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-lg"
              >
                Access Dashboard
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 1. Section: The Intelligent Dashboard "SignO" */}
      <section className="py-24 bg-dark relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollFloat
              animationDuration={1}
              ease="back.inOut(2)"
              scrollStart="center bottom+=50%"
              scrollEnd="bottom bottom-=40%"
              stagger={0.03}
              containerClassName="mb-4"
              textClassName="text-4xl md:text-5xl font-bold"
            >
              Complete Visibility. Zero Clutter
            </ScrollFloat>
            <div className="max-w-2xl mx-auto">
              <ScrollReveal
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                textClassName="text-xl text-gray-400 font-normal"
              >
                A unified view of every document you’ve sent, received, or
                signed. Track progress in real-time
              </ScrollReveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ElectricBorder color="#eab308" style={{ borderRadius: 16 }}>
              <DashboardCard
                icon={<LayoutDashboard className="w-8 h-8 text-yellow-500" />}
                title="Pending Actions"
                body="Never miss a deadline. View a prioritized list of documents waiting for your signature or approval right now"
                label="Live Status"
              />
            </ElectricBorder>
            <ElectricBorder color="#3b82f6" style={{ borderRadius: 16 }}>
              <DashboardCard
                icon={<History className="w-8 h-8 text-blue-500" />}
                title="Document History"
                body="Access your complete archive. Filter by 'Sent by Admin,' 'Received from Others,' or date range. Every file is stored securely"
              />
            </ElectricBorder>
            <ElectricBorder color="#22c55e" style={{ borderRadius: 16 }}>
              <DashboardCard
                icon={<Play className="w-8 h-8 text-green-500" />}
                title="Start New Workflow"
                body="One click to Sign New Document. Drag and drop your PDF into the upload area to instantly trigger the signing editor"
              />
            </ElectricBorder>
            <ElectricBorder color="#a855f7" style={{ borderRadius: 16 }}>
              <DashboardCard
                icon={<Users className="w-8 h-8 text-purple-500" />}
                title="Team & Contact List"
                body="Manage your organization. Add new members, assign roles, or view the status of documents sent to your external contacts"
              />
            </ElectricBorder>
          </div>
        </div>
      </section>

      {/* 2. Section: Advanced KYC & Identity Verification */}
      <section className="py-24 bg-gradient-to-b from-dark to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollFloat
              animationDuration={1}
              ease="back.inOut(2)"
              scrollStart="center bottom+=50%"
              scrollEnd="bottom bottom-=40%"
              stagger={0.03}
              containerClassName="mb-4"
              textClassName="text-4xl md:text-5xl font-bold"
            >
              Trust Built on Verified Identity
            </ScrollFloat>
            <div className="max-w-2xl mx-auto">
              <ScrollReveal
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                textClassName="text-xl text-gray-400 font-normal"
              >
                We don't just capture signatures; we verify the human behind
                them
              </ScrollReveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-primary/0 via-primary/30 to-primary/0 z-0"></div>

            <StepCard
              number="01"
              icon={<UserCheck className="w-10 h-10 text-primary" />}
              title="Government-Grade ID Check"
              description="Seamlessly upload your Aadhaar Card and PAN Card. Our system performs an instant background check against official databases to validate your identity"
            />
            <StepCard
              number="02"
              icon={<Video className="w-10 h-10 text-secondary" />}
              title="Liveness Detection"
              description="Prevent identity theft with our Video Confirmation step. A quick liveness check ensures that the person submitting the ID is the person sitting behind the screen"
            />
            <StepCard
              number="03"
              icon={<Shield className="w-10 h-10 text-green-500" />}
              title="The Verification Badge"
              description="Once approved by our compliance engine, your profile earns a 'Verified' status. Every document you sign carries this seal of authenticity"
            />
          </div>
        </div>
      </section>

      {/* 3. Section: Profile & Asset Management */}
      <section className="py-24 bg-dark">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="md:w-1/2">
              <ScrollFloat
                animationDuration={1}
                ease="back.inOut(2)"
                scrollStart="center bottom+=50%"
                scrollEnd="bottom bottom-=40%"
                stagger={0.03}
                containerClassName="mb-6 text-left"
                textClassName="text-4xl font-bold"
              >
                Your Digital Brand, Professional & Ready
              </ScrollFloat>
              <div className="mb-8">
                <ScrollReveal
                  baseOpacity={0}
                  enableBlur={true}
                  baseRotation={5}
                  blurStrength={10}
                  textClassName="text-xl text-gray-400 font-normal"
                >
                  Create, store, and manage your official signatures and stamps
                  for one-click application
                </ScrollReveal>
              </div>

              <div className="space-y-6">
                <ElectricBorder color="#6366f1" style={{ borderRadius: 16 }}>
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <PenTool className="w-6 h-6 text-primary" />
                      <h3 className="text-xl font-bold">Signatures</h3>
                    </div>
                    <ul className="space-y-2 text-gray-300 ml-10 list-disc">
                      <li>
                        <strong className="text-white">Draw:</strong> Precision
                        drawing tool for a natural, handwritten feel
                      </li>
                      <li>
                        <strong className="text-white">Upload:</strong> Upload a
                        high-res image of your wet-ink signature
                      </li>
                      <li>
                        <span className="text-primary text-sm bg-primary/10 px-2 py-1 rounded">
                          Feature
                        </span>{" "}
                        Set a Default Signature to speed up your workflow
                      </li>
                    </ul>
                  </div>
                </ElectricBorder>

                <ElectricBorder color="#a855f7" style={{ borderRadius: 16 }}>
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <Stamp className="w-6 h-6 text-secondary" />
                      <h3 className="text-xl font-bold">Official Stamps</h3>
                    </div>
                    <ul className="space-y-2 text-gray-300 ml-10 list-disc">
                      <li>
                        <strong className="text-white">Digital Stamp:</strong>{" "}
                        Create a custom circular or rectangular stamp with your
                        Company Name and ID
                      </li>
                      <li>
                        <strong className="text-white">Upload Stamp:</strong>{" "}
                        Upload your official company seal
                      </li>
                      <li>
                        <span className="text-secondary text-sm bg-secondary/10 px-2 py-1 rounded">
                          Feature
                        </span>{" "}
                        Toggle "Set Default" to automatically apply your
                        official stamp
                      </li>
                    </ul>
                  </div>
                </ElectricBorder>
              </div>
            </div>
            <div className="md:w-1/2 relative">
              {/* Abstract Visual Representation */}
              <div className="relative w-full aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-full blur-3xl animate-blob"></div>
                <div className="relative z-10 glass-card p-8 h-full flex flex-col justify-center items-center">
                  <div className="w-full bg-white/5 rounded-lg p-4 mb-4">
                    <div className="h-20 border-b border-dashed border-gray-600 flex items-end pb-2">
                      <span className="font-handwriting text-4xl text-blue-400">
                        John Doe
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Digital Signature
                    </p>
                  </div>
                  <div className="w-32 h-32 rounded-full border-4 border-double border-purple-500 flex items-center justify-center p-2 text-center text-xs font-bold text-purple-400 tracking-widest uppercase">
                    Starlabs
                    <br />
                    Official
                    <br />
                    Seal
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Section: The Signing "Editor" Experience */}
      <section
        className="py-32 bg-slate-900 relative overflow-hidden"
        ref={editorRef}
        onMouseMove={(e) => {
          if (!editorRef.current) return;
          const rect = editorRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const el = revealImgRef.current;
          if (el) {
            el.style.setProperty("--mx", `${x}px`);
            el.style.setProperty("--my", `${y}px`);
          }
        }}
        onMouseLeave={() => {
          const el = revealImgRef.current;
          if (el) {
            el.style.setProperty("--mx", "-9999px");
            el.style.setProperty("--my", "-9999px");
          }
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <ScrollFloat
              animationDuration={1}
              ease="back.inOut(2)"
              scrollStart="center bottom+=50%"
              scrollEnd="bottom bottom-=40%"
              stagger={0.03}
              containerClassName="mb-4"
              textClassName="text-4xl md:text-5xl font-bold"
            >
              A Powerful Editor for Complex Workflows
            </ScrollFloat>
            <div className="max-w-2xl mx-auto">
              <ScrollReveal
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                textClassName="text-xl text-gray-400 font-normal"
              >
                Prepare documents for yourself or your team with our intuitive
                Drag-and-Drop editor
              </ScrollReveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ElectricBorder color="#22d3ee" style={{ borderRadius: 16 }}>
              <EditorFeature
                icon={<FileText className="w-8 h-8 text-cyan-400" />}
                title="Interactive Preview"
                description="Open your uploaded PDF in a high-fidelity previewer. Zoom, scroll, and review every detail before placing blocks"
              />
            </ElectricBorder>
            <ElectricBorder color="#fb923c" style={{ borderRadius: 16 }}>
              <EditorFeature
                icon={<LayoutDashboard className="w-8 h-8 text-orange-400" />}
                title="Smart Placement"
                description="Select exactly where you need a signature, stamp, or date. Drag the block onto the canvas and resize it to fit perfectly"
              />
            </ElectricBorder>
            <ElectricBorder color="#818cf8" style={{ borderRadius: 16 }}>
              <EditorFeature
                icon={<Users className="w-8 h-8 text-indigo-400" />}
                title="Role Assignment"
                description="Assign specific blocks to colleagues or external clients. Enter their email, and we handle the rest"
              />
            </ElectricBorder>
            <ElectricBorder color="#34d399" style={{ borderRadius: 16 }}>
              <EditorFeature
                icon={<Send className="w-8 h-8 text-emerald-400" />}
                title="Automated Dispatch"
                description="Click Confirm to lock the layout. We automatically generate a secure signing link and send it to the recipient"
              />
            </ElectricBorder>
          </div>
        </div>
      </section>

      {/* 5. Section: CardSwap - Why Choose SignO */}
      <section className="py-32 bg-dark relative overflow-hidden">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-16">
          <div className="md:w-1/2">
            <ScrollFloat
              animationDuration={1}
              ease="back.inOut(2)"
              scrollStart="center bottom+=50%"
              scrollEnd="bottom bottom-=40%"
              stagger={0.03}
              containerClassName="mb-6 text-left"
              textClassName="text-4xl md:text-5xl font-bold"
            >
              Why Leaders Choose SignO
            </ScrollFloat>
            <div className="mb-8">
              <ScrollReveal
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                textClassName="text-xl text-gray-400 font-normal"
              >
                We combine speed, security, and compliance into one seamless
                platform. Explore the layers of protection we offer.
              </ScrollReveal>
            </div>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-primary" />
                <span>Bank-grade encryption for all documents</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-primary" />
                <span>Real-time audit trails with IP tracking</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-primary" />
                <span>Global compliance standards (ESIGN, eIDAS)</span>
              </li>
            </ul>
          </div>

          <div className="md:w-1/2 h-[500px] relative flex items-center justify-center">
            <CardSwap
              cardDistance={60}
              verticalDistance={70}
              delay={4000}
              pauseOnHover={true}
              width={320}
              height={400}
            >
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 flex flex-col justify-center items-center text-center border border-white/10 shadow-2xl">
                <Layers className="w-16 h-16 text-blue-400 mb-6" />
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Multi-Layer Security
                </h3>
                <p className="text-gray-400">
                  From 256-bit encryption to biometric authentication, your data
                  is fortified at every level.
                </p>
              </Card>
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 flex flex-col justify-center items-center text-center border border-white/10 shadow-2xl">
                <Zap className="w-16 h-16 text-yellow-400 mb-6" />
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Lightning Fast
                </h3>
                <p className="text-gray-400">
                  Sign documents in seconds, not days. Automated workflows keep
                  your business moving at light speed.
                </p>
              </Card>
              <Card className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 flex flex-col justify-center items-center text-center border border-white/10 shadow-2xl">
                <Globe className="w-16 h-16 text-green-400 mb-6" />
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Global Compliance
                </h3>
                <p className="text-gray-400">
                  Legally binding in 180+ countries. We ensure your agreements
                  hold up in any court of law.
                </p>
              </Card>
            </CardSwap>
          </div>
        </div>
      </section>

      {/* 6. Section: The Blockchain Guarantee */}
      <section className="py-32 relative overflow-hidden">
        {/* LetterGlitch Background */}
        <div className="absolute inset-0 z-0">
          <LetterGlitch
            glitchSpeed={50}
            centerVignette={true}
            outerVignette={false}
            smooth={true}
            glitchColors={["#2b4539", "#61dca3", "#61b3dc"]}
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="glass-card max-w-5xl mx-auto p-12 md:p-16 text-center border-t border-blue-500/30 bg-dark/60 backdrop-blur-md">
            <div className="inline-flex p-4 bg-blue-500/20 rounded-full mb-8">
              <Database className="w-12 h-12 text-blue-400" />
            </div>
            <div className="mb-6 flex justify-center">
              <FuzzyText
                baseIntensity={0.2}
                hoverIntensity={0.5}
                enableHover={true}
                fontSize="clamp(2.5rem, 5vw, 4rem)"
                fontWeight={900}
                color="#fff"
              >
                Immutable. Traceable. Forever
              </FuzzyText>
            </div>
            <p className="text-2xl text-gray-300 mb-8 font-light">
              Moving beyond PDFs. We anchor every agreement on the Blockchain
            </p>
            <div className="bg-dark/80 p-8 rounded-2xl max-w-3xl mx-auto text-left border border-white/10">
              <p className="text-lg text-gray-300 leading-relaxed">
                "When a document is fully signed, we generate a unique
                cryptographic hash of the file and the audit trail. This hash is
                minted onto the Blockchain
              </p>
              <div className="mt-6 flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white font-bold text-lg">
                    Why this matters
                  </h4>
                  <p className="text-gray-400">
                    If a single pixel of that document is changed later, the
                    hash won't match. This provides mathematical proof that your
                    contract is original and untampered
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Section: Secure QR Access */}
      <section className="py-24 bg-dark relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16">
            <div className="md:w-1/2">
              <ScrollFloat
                animationDuration={1}
                ease="back.inOut(2)"
                scrollStart="center bottom+=50%"
                scrollEnd="bottom bottom-=40%"
                stagger={0.03}
                containerClassName="mb-6 text-left"
                textClassName="text-4xl font-bold"
              >
                Instant Access with Secure QR
              </ScrollFloat>
              <div className="mb-8">
                <ScrollReveal
                  baseOpacity={0}
                  enableBlur={true}
                  baseRotation={5}
                  blurStrength={10}
                  textClassName="text-xl text-gray-400 font-normal"
                >
                  For every document created, a secure QR code is generated.
                  Share, access, and retrieve documents instantly with a simple
                  scan.
                </ScrollReveal>
              </div>

              <div className="space-y-6">
                <ElectricBorder color="#ec4899" style={{ borderRadius: 16 }}>
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <QrCode className="w-6 h-6 text-pink-500" />
                      <h3 className="text-xl font-bold">Easy Sharing</h3>
                    </div>
                    <p className="text-gray-300 ml-10">
                      No need for complex links or attachments. Just share the
                      QR code for instant, secure access to the document.
                    </p>
                  </div>
                </ElectricBorder>
              </div>
            </div>

            <div className="md:w-1/2 flex justify-center">
              <div className="relative w-64 h-64 bg-white p-4 rounded-2xl shadow-[0_0_50px_rgba(236,72,153,0.3)]">
                <div className="absolute inset-0 border-4 border-dashed border-pink-500/30 rounded-2xl animate-pulse"></div>
                <QrCode className="w-full h-full text-dark" strokeWidth={1} />
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-dark px-4 py-1 rounded-full border border-pink-500/50 text-pink-400 text-sm font-bold whitespace-nowrap">
                  Scan to Access
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Section: Document Validation */}
      <section className="py-24 bg-gradient-to-b from-dark to-slate-900">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="md:w-1/2">
              <ScrollFloat
                animationDuration={1}
                ease="back.inOut(2)"
                scrollStart="center bottom+=50%"
                scrollEnd="bottom bottom-=40%"
                stagger={0.03}
                containerClassName="mb-6 text-left"
                textClassName="text-4xl font-bold"
              >
                Real-time Document Validation
              </ScrollFloat>
              <div className="mb-8">
                <ScrollReveal
                  baseOpacity={0}
                  enableBlur={true}
                  baseRotation={5}
                  blurStrength={10}
                  textClassName="text-xl text-gray-400 font-normal"
                >
                  Validate the document after it's signed at any time. Use the
                  unique ID or QR code to get instant proof of authenticity.
                </ScrollReveal>
              </div>

              <div className="space-y-6">
                <ElectricBorder color="#22c55e" style={{ borderRadius: 16 }}>
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <ScanLine className="w-6 h-6 text-green-500" />
                      <h3 className="text-xl font-bold">
                        Instant Verification
                      </h3>
                    </div>
                    <p className="text-gray-300 ml-10">
                      Verify signatures, timestamps, and integrity in seconds.
                      Ensure the document hasn't been tampered with since
                      signing.
                    </p>
                  </div>
                </ElectricBorder>
              </div>
            </div>

            <div className="md:w-1/2 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500/20 blur-3xl rounded-full"></div>
                <Shield className="w-48 h-48 text-green-500 relative z-10 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <CheckCircle className="w-24 h-24 text-white drop-shadow-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Circular Flow */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-t from-dark to-slate-900">
        <div className="container mx-auto px-4 relative z-10 flex flex-col items-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">
            Ready to Secure Your Documents?
          </h2>

          <div className="mb-16 scale-75 md:scale-100">
            <CircularFlow />
          </div>

          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-10 py-5 bg-white text-dark text-xl font-bold rounded-full hover:bg-gray-100 transition-all hover:scale-105 shadow-2xl"
          >
            Get Started Now
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const DashboardCard = ({ icon, title, body, label }) => (
  <div className="h-full bg-dark/50 p-6 relative overflow-hidden group rounded-2xl">
    {label && (
      <div className="absolute top-0 right-0 bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1 rounded-bl-lg">
        {label}
      </div>
    )}
    <div className="mb-4 p-3 bg-white/5 rounded-lg w-fit group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <h3 className="text-xl font-bold mb-3">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{body}</p>
  </div>
);

const StepCard = ({ number, icon, title, description }) => (
  <div className="h-full relative z-10 bg-dark border border-white/10 p-8 rounded-2xl hover:border-primary/50 transition-colors group text-center">
    <div className="w-16 h-16 mx-auto mb-6 bg-white/5 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors relative">
      {icon}
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-dark border border-white/10 rounded-full flex items-center justify-center text-sm font-bold text-gray-400">
        {number}
      </div>
    </div>
    <h3 className="text-xl font-bold mb-3">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </div>
);

const EditorFeature = ({ icon, title, description }) => (
  <div className="h-full flex flex-col items-center text-center p-6 relative z-10 bg-dark/50 rounded-2xl">
    <div className="mb-6 p-4 bg-white/5 rounded-2xl shadow-lg shadow-primary/5">
      {icon}
    </div>
    <h3 className="text-lg font-bold mb-2">{title}</h3>
    <p className="text-gray-400 text-sm">{description}</p>
  </div>
);

export default Home;
